import { useState } from "react";
import { Link } from "react-router-dom";
import Moment from "react-moment";
import {
  reactToComment,
  reactToReply,
  replyToComment,
  deleteComment,
  deleteReply,
} from "../../functions/post";
import CreateComment from "./CreateComment";

export default function Comment({
  comment,
  postId,
  user,
  setComments,
  isPostOwner,
}) {
  const [showReplies, setShowReplies] = useState(false);
  const [showReplyInput, setShowReplyInput] = useState(false);
  const [showReactPopup, setShowReactPopup] = useState(false);
  const [reacts, setReacts] = useState(comment.reacts || []);
  const [replies, setReplies] = useState(comment.replies || []);

  const reactTypes = ["like", "love", "haha", "wow", "sad", "angry"];

  // Verificar se o usuário já reagiu
  const userReact = reacts.find(
    (r) => r.reactBy?.toString() === user.id || r.reactBy?._id === user.id
  );

  // Contar reações
  const totalReacts = reacts.length;
  const topReacts = reactTypes
    .map((type) => ({
      type,
      count: reacts.filter((r) => r.react === type).length,
    }))
    .filter((r) => r.count > 0)
    .sort((a, b) => b.count - a.count)
    .slice(0, 3);

  const handleReact = async (react) => {
    const res = await reactToComment(postId, comment._id, react, user.token);
    if (res.reacts) {
      setReacts(res.reacts);
    }
    setShowReactPopup(false);
  };

  const handleReplySubmit = async (replyData) => {
    const updatedComment = await replyToComment(
      postId,
      comment._id,
      replyData,
      user.token
    );
    if (updatedComment.replies) {
      setReplies(updatedComment.replies);
    }
    setShowReplyInput(false);
    setShowReplies(true);
  };

  const handleDeleteComment = async () => {
    if (window.confirm("Are you sure you want to delete this comment?")) {
      await deleteComment(postId, comment._id, user.token);
      setComments((prev) => prev.filter((c) => c._id !== comment._id));
    }
  };

  // Renderizar texto com menções
  const renderTextWithMentions = (text, mentions) => {
    if (!mentions || mentions.length === 0) return text;

    let result = text;
    mentions.forEach((mention) => {
      if (mention && mention.username) {
        const regex = new RegExp(`@${mention.username}`, "g");
        result = result.replace(
          regex,
          `<a href="/profile/${mention.username}" class="mention_link">@${mention.first_name} ${mention.last_name}</a>`
        );
      }
    });
    return <span dangerouslySetInnerHTML={{ __html: result }} />;
  };

  return (
    <div className={`comment ${comment.isPinned ? "pinned_comment" : ""}`}>
      {comment.isPinned && (
        <div className="pinned_badge">
          <i className="pin_icon"></i> Pinned
        </div>
      )}
      <Link to={`/profile/${comment.commentBy?.username}`}>
        <img
          src={comment.commentBy?.picture}
          alt=""
          className="comment_img"
        />
      </Link>
      <div className="comment_col">
        <div className="comment_wrap">
          <Link
            to={`/profile/${comment.commentBy?.username}`}
            className="comment_name"
          >
            {comment.commentBy?.first_name} {comment.commentBy?.last_name}
          </Link>
          <div className="comment_text">
            {renderTextWithMentions(comment.text, comment.mentions)}
          </div>

          {/* Exibir reações no comentário */}
          {totalReacts > 0 && (
            <div className="comment_reacts_count">
              {topReacts.map((r) => (
                <img
                  key={r.type}
                  src={`../../../reacts/${r.type}.svg`}
                  alt={r.type}
                />
              ))}
              <span>{totalReacts}</span>
            </div>
          )}
        </div>

        {/* Imagem do comentário */}
        {comment.image && (
          <img src={comment.image} alt="" className="comment_image" />
        )}

        {/* GIF do comentário */}
        {comment.gif && (
          <img src={comment.gif} alt="GIF" className="comment_image comment_gif" />
        )}

        {/* Áudio do comentário */}
        {comment.audio && (
          <audio controls src={comment.audio} className="comment_audio" />
        )}

        {/* Ações do comentário */}
        <div className="comment_actions">
          <div
            className="comment_action_item"
            onMouseEnter={() => setShowReactPopup(true)}
            onMouseLeave={() => setShowReactPopup(false)}
          >
            <span
              className={`comment_action ${userReact ? `reacted_${userReact.react}` : ""}`}
              onClick={() => handleReact(userReact?.react || "like")}
            >
              {userReact ? userReact.react : "Like"}
            </span>

            {/* Popup de reações */}
            {showReactPopup && (
              <div className="comment_reacts_popup">
                {reactTypes.map((react) => (
                  <div
                    key={react}
                    className="comment_react_item"
                    onClick={() => handleReact(react)}
                  >
                    <img src={`../../../reacts/${react}.svg`} alt={react} />
                  </div>
                ))}
              </div>
            )}
          </div>

          <span
            className="comment_action"
            onClick={() => setShowReplyInput(!showReplyInput)}
          >
            Reply
          </span>

          <span className="comment_time">
            <Moment fromNow interval={30}>
              {comment.commentAt}
            </Moment>
          </span>

          {comment.isEdited && <span className="edited_badge">Edited</span>}

          {/* Menu de opções */}
          {(comment.commentBy?._id === user.id || isPostOwner) && (
            <div className="comment_menu">
              <span className="comment_action" onClick={handleDeleteComment}>
                Delete
              </span>
            </div>
          )}
        </div>

        {/* Input de resposta */}
        {showReplyInput && (
          <div className="reply_input_wrap">
            <CreateComment
              user={user}
              postId={postId}
              setComments={setComments}
              setCount={() => {}}
              replyTo={comment.commentBy?.username}
              commentId={comment._id}
              onReply={handleReplySubmit}
              onCancel={() => setShowReplyInput(false)}
              placeholder={`Reply to ${comment.commentBy?.first_name}...`}
            />
          </div>
        )}

        {/* Respostas */}
        {replies.length > 0 && (
          <div className="replies_section">
            {!showReplies && (
              <div
                className="view_replies"
                onClick={() => setShowReplies(true)}
              >
                <i className="reply_arrow"></i>
                View {replies.length}{" "}
                {replies.length === 1 ? "reply" : "replies"}
              </div>
            )}

            {showReplies && (
              <div className="replies_list">
                {replies.map((reply) => (
                  <Reply
                    key={reply._id}
                    reply={reply}
                    postId={postId}
                    commentId={comment._id}
                    user={user}
                    setReplies={setReplies}
                    isPostOwner={isPostOwner}
                  />
                ))}
                <div
                  className="hide_replies"
                  onClick={() => setShowReplies(false)}
                >
                  Hide replies
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

// Componente de resposta
function Reply({ reply, postId, commentId, user, setReplies, isPostOwner }) {
  const [showReactPopup, setShowReactPopup] = useState(false);
  const [reacts, setReacts] = useState(reply.reacts || []);

  const reactTypes = ["like", "love", "haha", "wow", "sad", "angry"];

  const userReact = reacts.find(
    (r) => r.reactBy?.toString() === user.id || r.reactBy?._id === user.id
  );

  const totalReacts = reacts.length;
  const topReacts = reactTypes
    .map((type) => ({
      type,
      count: reacts.filter((r) => r.react === type).length,
    }))
    .filter((r) => r.count > 0)
    .sort((a, b) => b.count - a.count)
    .slice(0, 3);

  const handleReact = async (react) => {
    const res = await reactToReply(
      postId,
      commentId,
      reply._id,
      react,
      user.token
    );
    if (res.reacts) {
      setReacts(res.reacts);
    }
    setShowReactPopup(false);
  };

  const handleDeleteReply = async () => {
    if (window.confirm("Are you sure you want to delete this reply?")) {
      await deleteReply(postId, commentId, reply._id, user.token);
      setReplies((prev) => prev.filter((r) => r._id !== reply._id));
    }
  };

  return (
    <div className="reply">
      <Link to={`/profile/${reply.replyBy?.username}`}>
        <img src={reply.replyBy?.picture} alt="" className="reply_img" />
      </Link>
      <div className="reply_col">
        <div className="reply_wrap">
          <Link
            to={`/profile/${reply.replyBy?.username}`}
            className="reply_name"
          >
            {reply.replyBy?.first_name} {reply.replyBy?.last_name}
          </Link>
          <div className="reply_text">{reply.text}</div>

          {totalReacts > 0 && (
            <div className="comment_reacts_count">
              {topReacts.map((r) => (
                <img
                  key={r.type}
                  src={`../../../reacts/${r.type}.svg`}
                  alt={r.type}
                />
              ))}
              <span>{totalReacts}</span>
            </div>
          )}
        </div>

        {reply.image && (
          <img src={reply.image} alt="" className="reply_image" />
        )}

        {reply.gif && (
          <img src={reply.gif} alt="GIF" className="reply_image reply_gif" />
        )}

        {reply.audio && (
          <audio controls src={reply.audio} className="reply_audio" />
        )}

        <div className="comment_actions">
          <div
            className="comment_action_item"
            onMouseEnter={() => setShowReactPopup(true)}
            onMouseLeave={() => setShowReactPopup(false)}
          >
            <span
              className={`comment_action ${userReact ? `reacted_${userReact.react}` : ""}`}
              onClick={() => handleReact(userReact?.react || "like")}
            >
              {userReact ? userReact.react : "Like"}
            </span>

            {showReactPopup && (
              <div className="comment_reacts_popup">
                {reactTypes.map((react) => (
                  <div
                    key={react}
                    className="comment_react_item"
                    onClick={() => handleReact(react)}
                  >
                    <img src={`../../../reacts/${react}.svg`} alt={react} />
                  </div>
                ))}
              </div>
            )}
          </div>

          <span className="comment_time">
            <Moment fromNow interval={30}>
              {reply.replyAt}
            </Moment>
          </span>

          {(reply.replyBy?._id === user.id || isPostOwner) && (
            <span className="comment_action" onClick={handleDeleteReply}>
              Delete
            </span>
          )}
        </div>
      </div>
    </div>
  );
}
